/**
 * Weekdays, seasons, moons and world time (1.K-1 deliverable 8). Each takes a tick and returns
 * null without a compiled calendar or for a value that is not a tick (deliverable 2). This settles
 * P0-13's second open question: the engine stores season starts only and never reads Foundry's
 * `dayEnd`, so every day belongs to exactly one season.
 */
import { isTick } from '@codex/contracts';
import { add, floorMod, floorModReal, sub } from './math.js';
import { isCompiled, MOON_PHASE_NAMES, } from './types.js';
import { locate } from './year.js';
function located(calendar, tick) {
    return isCompiled(calendar) && isTick(tick) ? locate(calendar, tick) : null;
}
/**
 * The weekday of a located day: `(startingWeekday + day − 1) mod W` when its month sets
 * `startingWeekday`, else `(firstWeekday + daysSinceEpoch) mod W`, with floor modulo.
 */
export function weekdayAt(calendar, at) {
    const w = calendar.weekdays.length;
    const sw = at.month.startingWeekday;
    // Reduced first, so no sum leaves the safe integers.
    const index = sw === null
        ? floorMod(calendar.firstWeekday + floorMod(at.days, w), w)
        : floorMod(floorMod(sw, w) + at.day - 1, w);
    return { index, key: calendar.weekdays[index].key };
}
/**
 * The season of a located day: the one with the greatest start at or before its day index; before
 * the first start, the one with the greatest start, which began in the previous year.
 */
export function seasonAt(calendar, at) {
    const seasons = calendar.seasons;
    if (seasons.length === 0)
        return null;
    let found = -1;
    for (const [i, season] of seasons.entries()) {
        if (season.startDay <= at.dayOfYear)
            found = i;
    }
    if (found < 0) {
        const last = seasons[seasons.length - 1];
        const previous = sub(at.year, 1);
        return Number.isSafeInteger(previous) ? { key: last.key, absoluteYear: previous } : null;
    }
    return { key: seasons[found].key, absoluteYear: at.year };
}
/** One phase per moon, in definition order: 0 new, 0.5 full; the name is one of eight. */
export function moonsAt(calendar, at) {
    return calendar.moons.map((moon) => {
        const phase = floorModReal(at.days - moon.offset, moon.cycleLength) / moon.cycleLength;
        const name = MOON_PHASE_NAMES[floorMod(Math.floor(phase * 8 + 0.5), 8)];
        return { key: moon.key, phase, name };
    });
}
export function weekdayOf(calendar, tick) {
    const at = located(calendar, tick);
    return at === null ? null : weekdayAt(calendar, at);
}
/** Null also when the calendar has no seasons. */
export function seasonOf(calendar, tick) {
    const at = located(calendar, tick);
    return at === null ? null : seasonAt(calendar, at);
}
export function moonsOf(calendar, tick) {
    const at = located(calendar, tick);
    return at === null ? null : moonsAt(calendar, at);
}
/** Foundry's `worldTime` of a tick: `tick − foundry.worldTimeOffset` (Base §6). */
export function toWorldTime(calendar, tick) {
    if (!isCompiled(calendar) || !isTick(tick))
        return null;
    const worldTime = sub(tick, calendar.worldTimeOffset);
    return Number.isSafeInteger(worldTime) ? worldTime : null;
}
/** The tick of a Foundry `worldTime`: `worldTime + foundry.worldTimeOffset`. */
export function fromWorldTime(calendar, worldTime) {
    if (!isCompiled(calendar) || !Number.isSafeInteger(worldTime))
        return null;
    const tick = add(worldTime, calendar.worldTimeOffset);
    return isTick(tick) ? tick : null;
}
//# sourceMappingURL=cycles.js.map